// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { Box, Checkbox, FormControlLabel, FormGroup, InputLabel, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseTable,
  BaseTableOption,
  BaseToolbar,
  ConfirmModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import { deleteService, getServiceList } from '~/thunks/service/serviceThunk';
import { selectIsRefreshServiceList, serviceActions } from '~/thunks/service/serviceSlice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { StorageEnum } from '~/utils/enum/base';
import { IPaginationResponse } from '~/utils/interface/base';
import useDebounce from '~/utils/hooks/useDebounce';
import { formatCurrency, formatTitleCase } from '~/utils/helper/common';
import { IBaseOption, IFilterValueChange, IHeaderCellType } from '~/utils/interface/common';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  SEARCH_BY_KEY,
} from '~/utils/constants/common';
import { IQueryParamService, IService } from '~/utils/interface/service';
import { mockDataServiceUnit } from '~/utils/mockData/service';
import { RoleEnum, SearchByFilterContentEnum, SearchByFilterEnum } from '~/utils/enum/common';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { adminRouteAbsolute } from '~/utils/routes/admin';
// Styles, images, icons
import styles from './Service.module.scss';
import { icons, images } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void
): IHeaderCellType<IService>[] => {
  return [
    {
      id: 'name',
      label: t('table_service_title_name'),
      render: ({ serviceImage, name }: IService) => (
        <Box component={'div'} className={cx('viewName')}>
          {serviceImage ? (
            <img src={serviceImage} alt={t('common_alt_img')} className={cx('image')} />
          ) : (
            <img src={images.imgDefaultInventory} alt={t('common_alt_img')} className={cx('image')} />
          )}

          {name || EMPTY_STRING}
        </Box>
      ),
      minWidth: 150,
    },
    {
      id: 'description',
      label: t('table_service_title_description'),
      render: (record: IService) => record?.description || EMPTY_STRING,
    },
    {
      id: 'taskCode',
      label: t('table_service_title_task_code'),
      render: (record: IService) => record?.taskCode || EMPTY_STRING,
      minWidth: 150,
    },
    {
      id: 'price',
      label: t('table_service_title_total_price'),
      render: (record: IService) => formatCurrency(Number(record.price)) || EMPTY_STRING,
      minWidth: 150,
    },
    {
      id: 'action',
      label: t('table_service_title_action'),
      align: 'left',
      render: (record: IService) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            onDelete={() => onDelete(record.id)}
          />
        );
      },
    },
  ];
};

const Service = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/service']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshServiceList = useSelector(selectIsRefreshServiceList);
  //#endregion Selector

  //#region Declare State
  const [filterParams, setFilterParams] = useState<IQueryParamService>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IQueryParamService>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [totalItems, setTotalItems] = useState<string>();
  const [serviceList, setServiceList] = useState<IService[]>([]);

  const [serviceSelected, setServiceSelected] = useState<string | null>(null);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetServiceList(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshServiceList) return;

    handleGetServiceList(paramObject);
    dispatch(serviceActions.setRefreshServiceList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshServiceList]);

  useEffect(() => {
    setSearchKey(textSearchParams);
  }, [textSearchParams]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields?.map((item) => formatTitleCase(item))?.join(', ');
      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (!searchKey) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      ...(filterParams ? { ...filterParams } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetServiceList = (params: IQueryParamService) => {
    setIsLoading(true);

    dispatch(getServiceList(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setServiceList(responses);
        setPagination(pagination);
        setTotalItems(totalItems.toString());
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddService = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createService);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createService);
        break;
    }
  };

  const handleEditService = (serviceId: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.updateService(serviceId));
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.updateService(serviceId));
        break;
    }
  };

  const handleDeleteService = (id: string) => {
    if (!id) return;

    setServiceSelected(id);
    setIsShowConfirmDelete(true);
  };

  const handleViewService = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.service}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.service}/${id}`);
        break;
    }
  };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleApplyFilter = (values: IQueryParamService | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    setParamObject({
      ...paramObject,
      ...values,
    });
  };

  const onFilterChildrenChange = (
    event: ChangeEvent<HTMLInputElement>,
    valueFilter: IQueryParamService | undefined,
    onChange?: (value: IFilterValueChange<IQueryParamService>) => void
  ) => {
    const { name, value, checked } = event.target;
    const newFields: IQueryParamService = {
      ...filterParams,
      fields: checked
        ? [...(valueFilter?.fields || []), value]
        : (valueFilter?.fields || [])?.filter((item) => item !== value),
    };

    if (name === SEARCH_BY_KEY && onChange) {
      onChange({ name: 'fields', value: newFields?.fields });
    }
  };

  const handleFilterUnitChange = (
    option: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamService>) => void
  ) => {
    if (!option) return;

    const { value } = option;

    onChange?.({ name: 'unit', value: value });
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDelete(false);
    setServiceSelected(null);
  };

  const handleConfirmDelete = () => {
    if (!serviceSelected) return;

    setIsLoading(true);

    dispatch(deleteService(serviceSelected))
      .unwrap()
      .then((res) => {
        dispatch(serviceActions.setRefreshServiceList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseConfirmDelete();
      });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('menu_services', { ns: 'common/sidebar' })}>
    <Box component={'div'} id='servicePageAdmin' className={cx('container')}>
      <BaseToolbar
        title={t('service_title_total', { quantity: totalItems })}
        textAction={t('button_add_service')}
        onAction={handleAddService}
      >
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.NAME}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.NAME
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters<IQueryParamService>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
        >
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_search_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.TASK_CODE}
                      checked={value?.fields?.includes(SearchByFilterEnum.TASK_CODE) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_task_code')}
                />
              </FormGroup>

              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_unit')}</InputLabel>

                <BaseSelect
                  layout='horizontal'
                  width={'160px'}
                  options={mockDataServiceUnit}
                  value={value?.unit}
                  onChange={(option) => handleFilterUnitChange(option, onChange)}
                />
              </Box>
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(t, handleViewService, handleEditService, handleDeleteService)}
          dataSource={serviceList || []}
          onPageChange={handlePageChange}
          onRowClick={handleViewService}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
    </PageShell>
  );
};

export default Service;
